import { useEffect, useRef, useState } from "react";
import { getConversationHistory } from "@/api/sdk.gen";
import { appState } from "@/app/appState";
import { formatHistoryTime } from "@/components/richtext/Citation/CitationNode";

type HistoryRow = {
    id: number;
    time: string;
    epoch: number;
    role: "user" | "assistant";
    kind: string;
    text: string;
    activity: string;
    cited: boolean;
};

const Highlighted = ({ text, quote }: { text: string; quote?: string }) => {
    const at = quote ? text.indexOf(quote) : -1;
    if (!quote || at < 0) return <>{text}</>;
    return <>{text.slice(0, at)}<mark>{quote}</mark>{text.slice(at + quote.length)}</>;
};

/** Compressed history of a conversation; with a time window, scrolls to and marks the cited rows. */
export const HistoryViewer = ({ conversationId, startTime, endTime, citedText }: {
    conversationId: number;
    startTime?: number;
    endTime?: number;
    citedText?: string;
}) => {
    const [rows, setRows] = useState<HistoryRow[] | null>(null);
    const [failed, setFailed] = useState(false);
    const firstCitedRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        let live = true;
        getConversationHistory({ body: { conversation_id: conversationId, start_time: startTime, end_time: endTime } })
            .then((response) => {
                if (!live) return;
                if (response.error) setFailed(true);
                else setRows(((response.data as { rows?: HistoryRow[] })?.rows) ?? []);
            })
            .catch(() => live && setFailed(true));
        return () => { live = false; };
    }, [conversationId, startTime, endTime]);

    useEffect(() => {
        firstCitedRef.current?.scrollIntoView({ block: "center" });
    }, [rows]);

    if (failed) return <p>{appState.t("chat.history.failed")}</p>;
    if (rows === null) return <p className="opacity-60">{appState.t("chat.history.loading")}</p>;
    if (rows.length === 0) return <p className="opacity-60">{appState.t("chat.history.empty")}</p>;

    let firstCitedSeen = false;
    return (
        <div className="flex flex-col gap-2 max-h-[70vh] overflow-y-auto text-left">
            {rows.map((row) => {
                const isFirstCited = row.cited && !firstCitedSeen;
                if (isFirstCited) firstCitedSeen = true;
                return (
                    <div
                        key={row.id}
                        ref={isFirstCited ? firstCitedRef : undefined}
                        className={`rounded px-2 py-1 ${row.cited ? "bg-yellow-100/70" : ""}`}
                    >
                        <div className="text-xs opacity-60">
                            {row.role === "user" ? appState.t("chat.history.roleUser") : appState.t("chat.history.roleAssistant")}
                            {" · "}
                            {formatHistoryTime(row.epoch)}
                        </div>
                        {row.text && (
                            <div className="whitespace-pre-wrap">
                                <Highlighted text={row.text} quote={row.cited ? citedText : undefined} />
                            </div>
                        )}
                        {row.activity && <div className="whitespace-pre-wrap text-xs font-mono opacity-70">{row.activity}</div>}
                    </div>
                );
            })}
        </div>
    );
};

export const openHistoryViewer = (params: { conversationId: number; startTime?: number; endTime?: number; citedText?: string }) => {
    appState.setModal({
        title: appState.t("chat.history.title"),
        body: <HistoryViewer {...params} />,
    });
};
